// Tier 4 feature-flag evaluation for the JS engine.
//
// Port of core/src/feature_flags.rs. The Rust core is the spec; this file
// exists because that core cannot execute on React Native, where the JS engine
// is the ONLY engine — every flag read there returned `undefined` until this
// landed, so flag-gated code paths were dark for 100% of RN installs.
//
// The two halves that MUST match Rust bit-for-bit are the bucketing hash
// (feature_flags.rs:563-589) and the variant picker (feature_flags.rs:594-609):
// the server rolls users into buckets with the same algorithm, so a JS-side
// drift puts a user in a different rollout half than the server recorded.
import type {
  FeatureFlagCondition,
  FeatureFlagDefinition,
  FeatureFlagValue,
  FeatureFlagVariant
} from './types.js';

/** Exposure event to emit, returned by {@link FeatureFlagEngine.evaluate}. */
export interface FlagExposure {
  flagKey: string;
  flagResponse: FeatureFlagValue;
  /** Whether any condition matched (or there were none). */
  matched: boolean;
}

export interface FlagEvaluation {
  /** `undefined` when the flag is unknown — no definition and no bootstrap. */
  value: FeatureFlagValue | undefined;
  /** Present only when this `(key, response)` pair is new for the session. */
  exposure: FlagExposure | undefined;
}

// ── SHA-1 ────────────────────────────────────────────────────────────────
//
// Hand-rolled because the bundle must stand alone: the `react-native` export
// condition's module graph may not reach a Node builtin or any dependency
// Metro would have to resolve (see CLAUDE.md, "Platform packaging rules").

function rotl(value: number, bits: number): number {
  return (value << bits) | (value >>> (32 - bits));
}

/** Standard SHA-1. Returns the 20-byte digest. */
function sha1(input: Uint8Array): Uint8Array {
  const messageLen = input.length;
  // 1 byte for the 0x80 terminator + 8 bytes of length, rounded up to 64.
  const padded = new Uint8Array((((messageLen + 8) >> 6) + 1) << 6);
  padded.set(input);
  padded[messageLen] = 0x80;

  const view = new DataView(padded.buffer);
  const bitLen = messageLen * 8;
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x1_0000_0000));
  view.setUint32(padded.length - 4, bitLen % 0x1_0000_0000);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Int32Array(80);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getInt32(offset + i * 4);
    for (let i = 16; i < 80; i++) {
      w[i] = rotl(w[i - 3]! ^ w[i - 8]! ^ w[i - 14]! ^ w[i - 16]!, 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let i = 0; i < 80; i++) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl(a, 5) + f + e + k + w[i]!) | 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  outView.setInt32(0, h0);
  outView.setInt32(4, h1);
  outView.setInt32(8, h2);
  outView.setInt32(12, h3);
  outView.setInt32(16, h4);
  return out;
}

/** 15 hex characters all set — the divisor Rust uses (feature_flags.rs:574). */
const BUCKET_MAX = BigInt('0xFFFFFFFFFFFFFFF');

const utf8 = new TextEncoder();

/**
 * Hash `(distinct_id, salt)` into a `[0.0, 1.0)` bucket.
 *
 * Port of `hash_bucket` (feature_flags.rs:563-576), and PostHog-compatible:
 * SHA-1 of `"<salt>.<distinct_id>"` with the SALT FIRST, first 15 hex chars of
 * the digest parsed as an integer, divided by `0xFFFFFFFFFFFFFFF`.
 *
 * `BigInt` rather than `parseInt`: 15 hex characters is 60 bits, which exceeds
 * `Number.MAX_SAFE_INTEGER`, so a float parse would silently round and land
 * users in the wrong bucket. Converting the BigInt to Number afterwards
 * reproduces Rust's own `u64 as f64` rounding exactly.
 */
export function hashBucket(distinctId: string, salt: string): number {
  const digest = sha1(utf8.encode(`${salt}.${distinctId}`));
  let hex = '';
  for (let i = 0; i < 8; i++) hex += digest[i]!.toString(16).padStart(2, '0');
  const value = BigInt(`0x${hex.slice(0, 15)}`);
  return Number(value) / Number(BUCKET_MAX);
}

/** Port of `within_rollout` (feature_flags.rs:580-589). */
function withinRollout(distinctId: string, flagKey: string, percentage: number): boolean {
  if (!(percentage > 0)) return false;
  if (percentage >= 100) return true;
  return hashBucket(distinctId, flagKey) * 100 <= percentage;
}

/**
 * Port of `pick_variant` (feature_flags.rs:594-609). The salt is
 * `"<flag_key>.variant"` — deliberately distinct from the rollout salt so
 * "user is in the 30% rollout" and "user gets variant B" are independent draws.
 */
function pickVariant(
  variants: readonly FeatureFlagVariant[],
  distinctId: string,
  flagKey: string
): string {
  if (variants.length === 0) return '';
  const bucket = hashBucket(distinctId, `${flagKey}.variant`) * 100;
  const weight = (v: FeatureFlagVariant): number => Math.max(Number(v.weight) || 0, 0);
  const total = variants.reduce((sum, v) => sum + weight(v), 0);
  const scale = total > 0 ? 100 / total : 1;
  let cumulative = 0;
  for (const v of variants) {
    cumulative += weight(v) * scale;
    if (bucket <= cumulative) return v.key;
  }
  return variants[variants.length - 1]!.key;
}

// ── Condition evaluation ─────────────────────────────────────────────────

/**
 * Structural equality standing in for `serde_json::Value: PartialEq`.
 *
 * KNOWN NARROW DIVERGENCE: serde_json distinguishes integer from float
 * representations, so a config `5` does not equal a property `5.0` in Rust
 * while both are `5` here. Chasing it would mean modelling serde's number
 * enum in JS for a case no server emits — recorded rather than emulated.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const aKeys = Object.keys(ao);
  const bKeys = Object.keys(bo);
  return aKeys.length === bKeys.length && aKeys.every((k) => k in bo && deepEqual(ao[k], bo[k]));
}

/**
 * `serde_json::Value::as_f64` — numbers only. A numeric STRING is not a
 * number here (feature_flags.rs:528-535 relies on that), which is the
 * opposite of the SKAN engine's coercion. The two are deliberately different.
 */
function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function containsInsensitive(value: unknown, needle: string): boolean {
  return typeof value === 'string' && value.toLowerCase().includes(needle.toLowerCase());
}

/** Port of `evaluate_condition` (feature_flags.rs:495-548). */
function evaluateCondition(
  condition: FeatureFlagCondition,
  distinctId: string,
  flagKey: string,
  properties: Record<string, unknown>
): boolean {
  switch (condition.type) {
    case 'property_equals':
      return condition.property in properties
        ? deepEqual(properties[condition.property], condition.value)
        : false;
    case 'property_in':
      return condition.property in properties
        ? condition.values.some((v) => deepEqual(properties[condition.property], v))
        : false;
    case 'property_contains':
      return containsInsensitive(properties[condition.property], condition.value);
    case 'property_regex':
      // Substring, not a regex: the Rust core deliberately does not link a
      // regex engine for flags (feature_flags.rs:517-527) — the server does
      // the real match during /decide and pushes back a boolean condition.
      return containsInsensitive(properties[condition.property], condition.pattern);
    case 'property_greater_than': {
      const n = asNumber(properties[condition.property]);
      return n !== undefined && n > condition.value;
    }
    case 'property_less_than': {
      const n = asNumber(properties[condition.property]);
      return n !== undefined && n < condition.value;
    }
    case 'percentage_rollout':
      return withinRollout(distinctId, flagKey, condition.percentage);
    case 'cohort':
      // Not resolvable client-side; fails closed (feature_flags.rs:539-546).
      return false;
    default:
      // Unknown discriminant — fail closed rather than match a flag we do not
      // understand.
      return false;
  }
}

/** JS-truthiness of a flag value — port of `FlagValue::as_bool`. */
export function flagValueIsTruthy(value: FeatureFlagValue | undefined): boolean {
  if (value === undefined) return false;
  if (typeof value === 'boolean') return value;
  return value.length > 0 && value !== 'false';
}

/** The dedup key half derived from a response (feature_flags.rs:479-482). */
function responseKey(value: FeatureFlagValue): string {
  return typeof value === 'boolean' ? String(value) : value;
}

// ── Engine ───────────────────────────────────────────────────────────────

/**
 * Port of `FeatureFlagManager` (feature_flags.rs:236-463). Single-threaded, so
 * the RwLock is dropped; everything else is 1:1.
 */
export class FeatureFlagEngine {
  private definitions = new Map<string, FeatureFlagDefinition>();
  private bootstrapValues: Record<string, FeatureFlagValue> = {};
  private bootstrapPayloads: Record<string, unknown> = {};
  private personProperties: Record<string, unknown> = {};
  /** `${flagKey} ${response}` pairs already exposed this session. */
  private exposed = new Set<string>();

  /** Replace every definition (after a `/config` fetch). */
  setDefinitions(defs: readonly FeatureFlagDefinition[]): void {
    this.definitions.clear();
    for (const def of defs) {
      // Keyed exactly as Rust does (feature_flags.rs:270-282) — including an
      // empty key, which `set_definitions` inserts rather than skipping. The
      // public API rejects `getFeatureFlag('')` upstream either way, but
      // `knownFeatureFlagKeys()` must agree across engines.
      if (typeof def.key === 'string') this.definitions.set(def.key, def);
    }
  }

  setBootstrap(values: Record<string, FeatureFlagValue>, payloads: Record<string, unknown>): void {
    this.bootstrapValues = { ...values };
    this.bootstrapPayloads = { ...payloads };
  }

  setPersonProperties(props: Record<string, unknown>): void {
    this.personProperties = { ...props };
  }

  mergePersonProperties(props: Record<string, unknown>): void {
    this.personProperties = { ...this.personProperties, ...props };
  }

  /** Called on session rotation so a new session re-emits one exposure/flag. */
  clearExposureDedup(): void {
    this.exposed.clear();
  }

  knownKeys(): string[] {
    return [...this.definitions.keys()];
  }

  hasData(): boolean {
    return this.definitions.size > 0 || Object.keys(this.bootstrapValues).length > 0;
  }

  /** Port of `evaluate` (feature_flags.rs:345-410). */
  evaluate(
    flagKey: string,
    distinctId: string,
    extraProperties: Record<string, unknown> = {}
  ): FlagEvaluation {
    // 1. Bootstrap shadows definitions, but still produces an exposure.
    if (Object.prototype.hasOwnProperty.call(this.bootstrapValues, flagKey)) {
      const value = this.bootstrapValues[flagKey]!;
      return { value, exposure: this.buildExposure(flagKey, value, true) };
    }

    const def = this.definitions.get(flagKey);
    if (!def) return { value: undefined, exposure: undefined };

    if (def.active === false) {
      return { value: false, exposure: this.buildExposure(flagKey, false, false) };
    }

    // Per-call properties win over `setPersonPropertiesForFlags`.
    const props = { ...this.personProperties, ...extraProperties };
    const { value, matched } = this.resolve(def, flagKey, distinctId, props);
    return { value, exposure: this.buildExposure(flagKey, value, matched) };
  }

  /** Port of `payload_for` (feature_flags.rs:413-422). */
  payloadFor(flagKey: string): unknown {
    if (Object.prototype.hasOwnProperty.call(this.bootstrapPayloads, flagKey)) {
      return this.bootstrapPayloads[flagKey];
    }
    return this.definitions.get(flagKey)?.payload;
  }

  /**
   * Port of `evaluate_all` (feature_flags.rs:427-462). Emits NO exposures —
   * `getFeatureFlag(key)` is the canonical exposure path.
   */
  evaluateAll(distinctId: string): Record<string, FeatureFlagValue> {
    const out: Record<string, FeatureFlagValue> = { ...this.bootstrapValues };
    for (const [key, def] of this.definitions) {
      if (Object.prototype.hasOwnProperty.call(out, key)) continue;
      out[key] =
        def.active === false
          ? false
          : this.resolve(def, key, distinctId, this.personProperties).value;
    }
    return out;
  }

  /**
   * Condition OR-group → variant pick, shared by evaluate/evaluateAll.
   * `matched` is returned alongside the value because it is a separate wire
   * field (`$feature_flag_matched`) that cannot be recovered from the value:
   * a matched flag with a `false`-weighted variant and an unmatched flag both
   * read as falsy.
   */
  private resolve(
    def: FeatureFlagDefinition,
    flagKey: string,
    distinctId: string,
    props: Record<string, unknown>
  ): { value: FeatureFlagValue; matched: boolean } {
    const conditions = def.conditions ?? [];
    const matched =
      conditions.length === 0 ||
      conditions.some((c) => evaluateCondition(c, distinctId, flagKey, props));
    if (!matched) return { value: false, matched: false };
    const variants = def.variants ?? [];
    return {
      value: variants.length > 0 ? pickVariant(variants, distinctId, flagKey) : true,
      matched: true
    };
  }

  /** Port of `build_exposure` (feature_flags.rs:473-493). */
  private buildExposure(
    flagKey: string,
    value: FeatureFlagValue,
    matched: boolean
  ): FlagExposure | undefined {
    const key = `${flagKey} ${responseKey(value)}`;
    if (this.exposed.has(key)) return undefined;
    this.exposed.add(key);
    return { flagKey, flagResponse: value, matched };
  }
}

/**
 * Missing key. `JSON.parse` never yields `undefined` for a present key, so
 * this is exactly "the server omitted it" — which is the only thing
 * `#[serde(default)]` responds to. An explicit `null` is a VALUE and must
 * type-check against the declared Rust type.
 */
const isMissing = (v: unknown): boolean => v === undefined;

/** Every `type` the Rust `Condition` enum can deserialize. */
const CONDITION_TYPES = new Set([
  'property_equals',
  'property_in',
  'property_contains',
  'property_regex',
  'property_greater_than',
  'property_less_than',
  'percentage_rollout',
  'cohort'
]);

/**
 * Validate one condition the way serde does: an unknown `type`, or a known one
 * missing its required fields, fails deserialization.
 *
 * This is stricter than it looks like it needs to be, and deliberately so.
 * Rust's `Condition` is a tagged enum with no fallback variant, so a single bad
 * condition fails the WHOLE `FeatureFlagDefinition` and `remote_config.rs:
 * 192-199` then drops that flag entirely. Merely failing the condition here
 * would leave the flag present in `knownFeatureFlagKeys()` and `getAllFlags()`
 * on one engine and absent on the other.
 */
function isValidCondition(raw: unknown): raw is FeatureFlagCondition {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const c = raw as Record<string, unknown>;
  if (typeof c.type !== 'string' || !CONDITION_TYPES.has(c.type)) return false;
  switch (c.type) {
    case 'percentage_rollout':
      return typeof c.percentage === 'number';
    case 'cohort':
      return typeof c.cohort_id === 'string';
    case 'property_in':
      return typeof c.property === 'string' && Array.isArray(c.values);
    case 'property_contains':
      return typeof c.property === 'string' && typeof c.value === 'string';
    case 'property_regex':
      return typeof c.property === 'string' && typeof c.pattern === 'string';
    case 'property_greater_than':
    case 'property_less_than':
      return typeof c.property === 'string' && typeof c.value === 'number';
    default: // property_equals — `value` is untyped JSON but must be present.
      return typeof c.property === 'string' && 'value' in c;
  }
}

/** `Variant` has no serde defaults: both fields are required. */
function isValidVariant(raw: unknown): raw is FeatureFlagVariant {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const v = raw as Record<string, unknown>;
  return typeof v.key === 'string' && typeof v.weight === 'number';
}

/**
 * Parse the `feature_flags` section of a `/config` body into definitions.
 *
 * The server emits an OBJECT keyed by flag name (schema/config.schema.json:
 * 89-93); the map key is authoritative over any inline `key` field, and a
 * malformed entry is dropped while the rest pass — the same best-effort
 * contract as `remote_config.rs:185-208`.
 */
export function parseFeatureFlagConfig(raw: unknown): FeatureFlagDefinition[] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: FeatureFlagDefinition[] = [];
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const def = value as Record<string, unknown>;

    // NULL IS A VALUE, NOT AN ABSENCE — the same rule as surveys.ts. Verified
    // field-by-field against the real WASM binary: `payload` is
    // `Option<Value>` and a condition's `value` is an untyped `Value`, so null
    // is legal on those two; on every other field it is a type error that
    // fails the whole definition, and `remote_config.rs:192-199` drops the
    // flag. `?? []` treated `conditions: null` as absent and kept a flag Rust
    // never has.
    const rawConditions = isMissing(def.conditions) ? [] : def.conditions;
    const rawVariants = isMissing(def.variants) ? [] : def.variants;
    if (!Array.isArray(rawConditions) || !Array.isArray(rawVariants)) continue;
    if (!rawConditions.every(isValidCondition)) continue;
    if (!rawVariants.every(isValidVariant)) continue;
    // Bare `bool` with `#[serde(default = "default_active")]`.
    if (!isMissing(def.active) && typeof def.active !== 'boolean') continue;
    // `key: String` with `#[serde(default)]`. The map key overwrites it
    // afterwards (remote_config.rs:201-202), but serde still has to parse the
    // inline field first — so `"key": null` fails the definition even though
    // the parsed value would have been discarded.
    if (!isMissing(def.key) && typeof def.key !== 'string') continue;

    out.push({
      key,
      active: typeof def.active === 'boolean' ? def.active : true,
      conditions: rawConditions,
      variants: rawVariants,
      payload: def.payload
    });
  }
  return out;
}
